import React from 'react';
import { X, Calendar, Clock, Users, Phone, Mail, MapPin, Star, CreditCard, FileText, Utensils, Palette, Volume2 } from 'lucide-react';
import { BanquetHall, BanquetBooking, BanquetOrder } from '../types';
import { format } from 'date-fns';
import { cn } from '../utils/cn';

interface BanquetDetailModalProps {
  hall?: BanquetHall;
  orders: BanquetOrder[];
  bookings: BanquetBooking[];
  activeBooking?: BanquetBooking;
  isLoading: boolean;
  onClose: () => void;
}

const BanquetDetailModal: React.FC<BanquetDetailModalProps> = ({
  hall,
  orders,
  bookings,
  activeBooking,
  isLoading,
  onClose,
}) => {
  if (!hall) return null;

  const getEventTypeColor = (eventType: string) => {
    switch (eventType?.toLowerCase()) {
      case 'wedding':
        return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'corporate':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'birthday':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'conference':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType?.toLowerCase()) {
      case 'wedding':
        return '💒';
      case 'corporate':
        return '🏢';
      case 'birthday':
        return '🎂';
      case 'conference':
        return '📊';
      default:
        return '🎉';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderCategoryIcon = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'catering':
        return <Utensils className="w-4 h-4" />;
      case 'decoration':
        return <Palette className="w-4 h-4" />;
      case 'audiovisual':
        return <Volume2 className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getOrderCategoryColor = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'catering':
        return 'bg-orange-100 text-orange-800';
      case 'decoration':
        return 'bg-pink-100 text-pink-800';
      case 'audiovisual':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading banquet details...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{hall.hallName}</h2>
              <p className="text-primary-100 mt-1">{hall.hallType} • {hall.location}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6 space-y-6">
            {/* Hall Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Hall Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">Capacity: {hall.capacity} guests</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Star className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">Rate: ₹{hall.hourlyRate}/hour</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-700">{hall.location}</span>
                  </div>
                </div>
                
                {hall.amenities && hall.amenities.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Amenities</h4>
                    <div className="flex flex-wrap gap-2">
                      {hall.amenities.map((amenity, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Current Status</h3>
                <div className={cn(
                  "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium",
                  getStatusColor(hall.status)
                )}>
                  {hall.status === 'available' ? 'Available' : 
                   hall.status === 'occupied' ? 'Occupied' :
                   hall.status === 'maintenance' ? 'Under Maintenance' : 'Reserved'}
                </div>
                
                {hall.guestName && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Current Event</h4>
                    <p className="text-gray-800 font-medium">{hall.guestName}</p>
                    {hall.eventType && (
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-lg">{getEventTypeIcon(hall.eventType)}</span>
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          getEventTypeColor(hall.eventType)
                        )}>
                          {hall.eventType}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Active Booking Details */}
            {activeBooking && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Event Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Event Date</p>
                        <p className="font-medium">{format(new Date(activeBooking.eventDate), 'MMM dd, yyyy')}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Timing</p>
                        <p className="font-medium">{activeBooking.startTime} - {activeBooking.endTime}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Guest Count</p>
                        <p className="font-medium">{activeBooking.guestCount} guests</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Contact</p>
                        <p className="font-medium">{activeBooking.phone || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{activeBooking.email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{getEventTypeIcon(activeBooking.eventType)}</span>
                      <div>
                        <p className="text-sm text-gray-500">Event Type</p>
                        <span className={cn(
                          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                          getEventTypeColor(activeBooking.eventType)
                        )}>
                          {activeBooking.eventType}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Event Requirements */}
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Event Requirements</h4>
                  <div className="flex flex-wrap gap-2">
                    {activeBooking.cateringRequired && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                        <Utensils className="w-3 h-3" />
                        <span>Catering</span>
                      </span>
                    )}
                    {activeBooking.decorationRequired && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-pink-100 text-pink-800 text-sm rounded-full">
                        <Palette className="w-3 h-3" />
                        <span>Decoration</span>
                      </span>
                    )}
                    {activeBooking.audioVisualRequired && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                        <Volume2 className="w-3 h-3" />
                        <span>Audio/Visual</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Special Requirements */}
                {activeBooking.specialRequirements && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Special Requirements</h4>
                    <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                      {activeBooking.specialRequirements}
                    </p>
                  </div>
                )}

                {/* Financial Information */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CreditCard className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-500">Total Amount</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-800">₹{activeBooking.totalAmount.toLocaleString()}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CreditCard className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Advance Paid</span>
                    </div>
                    <p className="text-lg font-semibold text-green-800">₹{activeBooking.advanceAmount.toLocaleString()}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CreditCard className="w-4 h-4 text-orange-600" />
                      <span className="text-sm text-orange-600">Balance Due</span>
                    </div>
                    <p className="text-lg font-semibold text-orange-800">₹{activeBooking.balanceAmount.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Orders */}
            {orders.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Orders & Services</h3>
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div key={order.orderId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={cn(
                          "p-2 rounded-lg",
                          getOrderCategoryColor(order.category)
                        )}>
                          {getOrderCategoryIcon(order.category)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{order.itemName}</p>
                          <p className="text-sm text-gray-500">
                            {order.quantity} × ₹{order.unitPrice}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">₹{order.totalAmount}</p>
                        <p className="text-sm text-gray-500">{order.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Bookings for this Hall */}
            {bookings.length > 0 && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">All Bookings</h3>
                <div className="space-y-3">
                  {bookings.map((booking) => (
                    <div key={booking.bookingId} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{getEventTypeIcon(booking.eventType)}</span>
                          <h4 className="font-medium text-gray-800">{booking.eventName}</h4>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            getEventTypeColor(booking.eventType)
                          )}>
                            {booking.eventType}
                          </span>
                        </div>
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          getStatusColor(booking.bookingStatus)
                        )}>
                          {booking.bookingStatus}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="text-gray-500">Date:</span> {format(new Date(booking.eventDate), 'MMM dd, yyyy')}
                        </div>
                        <div>
                          <span className="text-gray-500">Time:</span> {booking.startTime} - {booking.endTime}
                        </div>
                        <div>
                          <span className="text-gray-500">Guests:</span> {booking.guestCount}
                        </div>
                        <div>
                          <span className="text-gray-500">Amount:</span> ₹{booking.totalAmount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BanquetDetailModal;
